/**
 * Unified permission utility for the partner-db app.
 *
 * Rules:
 * - Uses react-native-permissions exclusively (no PermissionsAndroid).
 * - androidApi is computed once at module load; Platform.Version is static so this is equivalent to call-time.
 * - Platform.Version is always a number on Android.
 * - iOS: most guards are no-ops or use the iOS equivalents where applicable.
 */
import { Platform } from "react-native";
import {
  checkMultiple,
  request,
  requestMultiple,
  PERMISSIONS,
  RESULTS,
  type Permission,
  type PermissionStatus,
} from "react-native-permissions";
import MovexGeo from "@movexgeo/react-native-movex";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const androidApi: number =
  Platform.OS === "android" ? (Platform.Version as number) : 0;

/**
 * react-native-permissions v5 removed POST_NOTIFICATIONS from PERMISSIONS.ANDROID
 * (the library expects callers to use checkNotifications/requestNotifications,
 * which branch on API 33+ internally). The native module still accepts the raw
 * string for check/request, so we re-export it under a stable name to avoid
 * `undefined` propagating to checkMultiple / request.
 */
export const POST_NOTIFICATIONS: Permission =
  "android.permission.POST_NOTIFICATIONS" as Permission;

export type PermissionResult = {
  granted: boolean;
  statuses: Partial<Record<Permission, PermissionStatus>>;
};

function allGranted(statuses: Partial<Record<Permission, PermissionStatus>>): boolean {
  return Object.values(statuses).every(
    (s) => s === RESULTS.GRANTED || s === RESULTS.LIMITED,
  );
}

async function requestPermissions(permissions: Permission[]): Promise<PermissionResult> {
  if (permissions.length === 0) {
    return { granted: true, statuses: {} };
  }
  const statuses = await requestMultiple(permissions);
  return { granted: allGranted(statuses), statuses };
}

async function checkPermissions(permissions: Permission[]): Promise<PermissionResult> {
  if (permissions.length === 0) {
    return { granted: true, statuses: {} };
  }
  const statuses = await checkMultiple(permissions);
  return { granted: allGranted(statuses), statuses };
}

// ---------------------------------------------------------------------------
// Storage permissions — API-level aware, matching manifest maxSdkVersion:
//
// WRITE_EXTERNAL_STORAGE: maxSdkVersion="29" → request only on API < 30.
//   On Android 11+ (API 30+) targeting SDK 30+, the OS drops this permission
//   at install time — requesting it returns DENIED with no Settings entry.
//
// READ_EXTERNAL_STORAGE:  maxSdkVersion="32" → request only on API < 33.
//   API >= 33: image-picker v7 uses the system Photo Picker (no broad media
//   permission needed). Play Store flagged READ_MEDIA_IMAGES/VIDEO as invalid
//   for our infrequent use case, so the manifest no longer declares them.
// ---------------------------------------------------------------------------

function storagePermissions(): Permission[] {
  if (Platform.OS !== "android") return [];
  if (androidApi >= 33) return [];
  const perms: Permission[] = [PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE];
  if (androidApi < 30) {
    perms.push(PERMISSIONS.ANDROID.WRITE_EXTERNAL_STORAGE);
  }
  return perms;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Request camera permission. */
export async function requestCamera(): Promise<PermissionResult> {
  if (Platform.OS === "ios") {
    const status = await request(PERMISSIONS.IOS.CAMERA);
    return {
      granted: status === RESULTS.GRANTED || status === RESULTS.LIMITED,
      statuses: { [PERMISSIONS.IOS.CAMERA]: status },
    };
  }
  const status = await request(PERMISSIONS.ANDROID.CAMERA);
  return {
    granted: status === RESULTS.GRANTED,
    statuses: { [PERMISSIONS.ANDROID.CAMERA]: status },
  };
}

/**
 * Request storage permissions.
 * API 33+: no-op — image-picker v7 uses the system photo picker.
 * API 30–32: READ_EXTERNAL_STORAGE only.
 * API < 30: READ + WRITE_EXTERNAL_STORAGE.
 */
export async function requestStorage(): Promise<PermissionResult> {
  return requestPermissions(storagePermissions());
}

/** Request fine + coarse location. */
export async function requestLocation(): Promise<PermissionResult> {
  if (Platform.OS === "ios") {
    const status = await request(PERMISSIONS.IOS.LOCATION_WHEN_IN_USE);
    return {
      granted: status === RESULTS.GRANTED || status === RESULTS.LIMITED,
      statuses: { [PERMISSIONS.IOS.LOCATION_WHEN_IN_USE]: status },
    };
  }
  // Coarse location is implied by fine location within the same permission group.
  // Requesting only fine lets the user choose precise vs approximate via the system dialog.
  return requestPermissions([PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION]);
}

/**
 * Request background location.
 * Only meaningful on Android API 29+. Must be called AFTER foreground location
 * is already granted (Android OS requirement).
 */
export async function requestBackgroundLocation(): Promise<PermissionResult> {
  if (Platform.OS === "ios") {
    const status = await request(PERMISSIONS.IOS.LOCATION_ALWAYS);
    return {
      granted: status === RESULTS.GRANTED || status === RESULTS.LIMITED,
      statuses: { [PERMISSIONS.IOS.LOCATION_ALWAYS]: status },
    };
  }
  if (androidApi < 29) {
    // ACCESS_BACKGROUND_LOCATION doesn't exist below API 29; foreground is enough.
    return { granted: true, statuses: {} };
  }
  const status = await request(PERMISSIONS.ANDROID.ACCESS_BACKGROUND_LOCATION);
  return {
    granted: status === RESULTS.GRANTED,
    statuses: { [PERMISSIONS.ANDROID.ACCESS_BACKGROUND_LOCATION]: status },
  };
}

/**
 * Request notification permission.
 * Only a runtime permission on Android API 33+. On lower APIs the permission
 * is granted implicitly by the OS, so we return granted immediately.
 */
export async function requestNotifications(): Promise<PermissionResult> {
  if (Platform.OS === "ios") {
    // iOS notifications use a separate API (notifee / PushNotificationIOS).
    // Return a no-op here so callers don't need to branch.
    return { granted: true, statuses: {} };
  }
  if (androidApi < 33) {
    return { granted: true, statuses: {} };
  }
  const status = await request(POST_NOTIFICATIONS);
  return {
    granted: status === RESULTS.GRANTED,
    statuses: { [POST_NOTIFICATIONS]: status },
  };
}

/**
 * Camera + storage combo used by image pickers.
 * Replaces both checkPermissions.js files.
 */
export async function requestImagePickerPermissions(): Promise<PermissionResult> {
  if (Platform.OS !== "android") {
    // iOS image picker handles its own permissions via photo library prompt.
    return { granted: true, statuses: {} };
  }
  const permissions: Permission[] = [
    PERMISSIONS.ANDROID.CAMERA,
    ...storagePermissions(),
  ];
  return requestPermissions(permissions);
}

/**
 * Storage-only permissions for file sharing (WhatsApp etc.).
 * On API 33+ there is no WRITE_EXTERNAL_STORAGE, so we only check read media.
 * Replaces handleFSPermission.js's permission logic.
 */
export async function requestFileSharePermissions(): Promise<PermissionResult> {
  return requestPermissions(storagePermissions());
}

// ---------------------------------------------------------------------------
// Tracking / PermissionsModal surface
// ---------------------------------------------------------------------------

export type PermissionItem = {
  key: string;
  label: string;
};

export const BATTERY_KEY = "battery_optimization";
export const BATTERY_REQUESTED_KEY = "battery_opt_requested";

// Labels rendered in the PermissionsModal. Keyed by the raw Android
// permission string (matches what react-native-permissions + Movex
// return). Anything not in the table falls back to the permission's
// own string.
const PERMISSION_LABELS: Record<string, string> = {
  [PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION]: "Precise location",
  [PERMISSIONS.ANDROID.ACCESS_COARSE_LOCATION]: "Approximate location",
  [PERMISSIONS.ANDROID.ACCESS_BACKGROUND_LOCATION]: "Background location",
  [PERMISSIONS.ANDROID.ACTIVITY_RECOGNITION]: "Physical activity",
  [POST_NOTIFICATIONS]: "Notifications",
  [PERMISSIONS.ANDROID.CAMERA]: "Camera",
  [PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE]: "Read storage",
  [PERMISSIONS.ANDROID.WRITE_EXTERNAL_STORAGE]: "Write storage",
};

function toItem(key: string): PermissionItem {
  return { key, label: PERMISSION_LABELS[key] ?? key };
}

/**
 * App-specific runtime permissions — owned by this app, not by the
 * movex.geo SDK. Camera (QR scan) and legacy storage on pre-API-33
 * devices (react-native-image-picker v7 uses the system photo picker
 * on API 33+, which doesn't need READ_MEDIA_*).
 *
 * POST_NOTIFICATIONS is intentionally omitted — Movex requests it on
 * API 33+ for its foreground-service notification, which is the same
 * permission FCM push needs.
 */
export function requiredByApp(): PermissionItem[] {
  if (Platform.OS !== "android") return [];
  const items: PermissionItem[] = [toItem(PERMISSIONS.ANDROID.CAMERA)];
  if (androidApi < 33) {
    items.push(toItem(PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE));
  }
  if (androidApi < 30) {
    items.push(toItem(PERMISSIONS.ANDROID.WRITE_EXTERNAL_STORAGE));
  }
  return items;
}

/**
 * Resolve the full list of PermissionItems the PermissionsModal needs
 * to render and request:
 *
 *   1. App-specific perms ([[requiredByApp]])
 *   2. Tracking perms reported by the SDK
 *      (MovexGeo.getRequiredPermissions('all') — fine/coarse location,
 *      background location, activity recognition, notifications;
 *      version-fenced by the SDK so no Android-API branching here)
 *   3. Battery-optimization opt-in (synthetic key; handled via Movex's
 *      isIgnoringBatteryOptimizations / requestIgnoreBatteryOptimizations)
 *
 * Duplicates are dropped (Movex may return POST_NOTIFICATIONS which an
 * earlier app row could also have included).
 *
 * iOS: returns [] — there's no equivalent multi-permission cascade,
 * and battery / OEM are Android-only concepts.
 */
export async function loadAllRequiredPermissions(): Promise<PermissionItem[]> {
  if (Platform.OS !== "android") return [];

  const fromApp = requiredByApp();
  let fromMovex: PermissionItem[] = [];
  try {
    const keys = await MovexGeo.getRequiredPermissions("all");
    fromMovex = keys.map(toItem);
  } catch {
    // SDK uninitialised or call failed — fall back to no tracking perms
    // rather than crashing the modal. PermissionsGate will re-run after
    // initMovex completes.
  }

  const seen = new Set<string>();
  const merged: PermissionItem[] = [];
  for (const item of [...fromApp, ...fromMovex]) {
    if (!seen.has(item.key)) {
      seen.add(item.key);
      merged.push(item);
    }
  }

  merged.push({ key: BATTERY_KEY, label: "Ignore battery optimizations" });
  return merged;
}

/**
 * Convenience: Permission[] for checkMultiple / requestMultiple,
 * derived from the merged list with the battery key dropped.
 */
export async function loadRequiredPermissionKeys(): Promise<Permission[]> {
  const items = await loadAllRequiredPermissions();
  return items
    .filter((p) => p.key !== BATTERY_KEY)
    .map((p) => p.key as Permission);
}

/**
 * Checks all required permissions and returns a status map.
 * Does NOT request — only checks current state.
 */
export async function checkAllRequiredPermissions(): Promise<PermissionResult> {
  return checkPermissions(await loadRequiredPermissionKeys());
}
